/**
 * Parser for NHS Prescription Item Report CSV files
 * These are the detailed prescription-level data files that pharmacies
 * use to reconcile against their FP34 Schedule of Payments.
 */

export interface PrescriptionItem {
  ocsCode: string;
  dispensingMonth: string; // YYYYMM
  fragmentId: string;
  formNumber: number;
  itemNumber: number;
  elementId: number;
  formType: string; // FP10MDA, FP10D, FP10, etc.
  prescriberCode: string;
  groupTypeDeclared: number;
  chargeStatus: number; // 1 = chargeable group
  chargesPayable: number;
  productDescription: string;
  snomedCode: string;
  quantity: number;
  unitOfMeasure: string;
  packPrice: number;
  packSize: number;
  basicPrice: number;
  paymentForConsumables: number;
  paymentForContainers: number;
  // Endorsements
  nb: string;
  cc: string; // No charge contraceptive
  da: string;
  daValue: number;
  ex: string;
  exValue: number;
  gn: string;
  ip: string;
  ipValue: number;
  lp: string;
  lpValue: number;
  mc: string;
  mcValue: number;
  mf: string;
  mi: string;
  miValue: number;
  mp: string;
  mpValue: number;
  nc: string;
  ncValue: number;
  nd: string;
  rb: string;
  rbValue: number;
  sf: string;
  sfValue: number;
  zd: string;
  // Fees
  sdrProfFeeValue: number;
  sdrProfFeeNumber: number;
  zdrProfFeeValue: number;
  zdrProfFeeNumber: number;
  spUnlicensedMedsFeeValue: number;
  edUnlicensedMedsFeeValue: number;
  mfHosieryFeeValue: number;
  mfTrussFeeValue: number;
  mfBeltGirdleFeeValue: number;
  methadoneFeeValue: number;
  methadonePckgdDoseFeeValue: number;
  homeDelSRApplAddFeeValue: number;
  homeDelHRApplAddFeeValue: number;
  cdSchedule2FeeValue: number;
  cdSchedule3FeeValue: number;
  expensiveItemFeeValue: number;
  stomaCustomisationFeeValue: number;
  dispensingUID: string;
  nhsPatientNumber: string;
  sspVatValue: number;
  sspFeeValue: number;
  dra: string;
  drb: string;
  drc: string;
  quantityPrescribed: number;
  quantityDispensed: number;
  opd: string;
  advanceService: string;
}

export interface FP34Summary {
  ocsCode: string;
  dispensingMonth: string;
  dispensingMonthLabel: string;
  // Counts
  totalForms: number;
  totalItems: number;
  chargePaidForms: number;
  chargePaidItems: number;
  noChargeForms: number;
  noChargeItems: number;
  // EPS vs Paper
  epsItems: number;
  epsForms: number;
  paperItems: number;
  paperForms: number;
  // Financial
  totalBasicPrice: number; // NIC
  totalConsumables: number;
  totalContainers: number;
  totalProfessionalFees: number;
  totalSdrProfFees: number;
  totalZdrProfFees: number;
  // Special fees
  totalExpensiveItemFees: number;
  totalCdSchedule2Fees: number;
  totalCdSchedule3Fees: number;
  totalMethadoneFees: number;
  totalMethadonePckgdDoseFees: number;
  totalSspFees: number;
  totalHomeDelFees: number;
  totalStomaFees: number;
  totalUnlicensedMedsFees: number;
  // Expensive items
  expensiveItemsOver100Count: number;
  expensiveItemsOver100Value: number;
  expensiveItemsOver300Count: number;
  expensiveItemsOver300Value: number;
  // Form types
  formTypeCounts: Record<string, number>;
  // Top products by value
  topProducts: { description: string; items: number; totalValue: number }[];
  // Raw items for drill-down
  items: PrescriptionItem[];
}

function parseNumber(val: string | undefined): number {
  if (!val || val.trim() === "" || val.trim() === "None") return 0;
  const n = parseFloat(val.trim());
  return isNaN(n) ? 0 : n;
}

function parseString(val: string | undefined): string {
  return (val || "").trim();
}

function formatDispensingMonth(yyyymm: string): string {
  if (!yyyymm || yyyymm.length < 6) return yyyymm;
  const year = yyyymm.substring(0, 4);
  const monthNum = parseInt(yyyymm.substring(4, 6));
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  return `${months[monthNum - 1] || "Unknown"} ${year}`;
}

export function parsePrescriptionCSV(csvText: string): PrescriptionItem[] {
  const lines = csvText.split("\n").map((l) => l.trim()).filter((l) => l.length > 0);
  if (lines.length < 2) return [];

  // Parse header
  const headers = lines[0].split(",").map((h) => h.trim());

  const items: PrescriptionItem[] = [];

  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(",");
    if (values.length < 10) continue;

    const get = (colName: string): string => {
      const idx = headers.indexOf(colName);
      return idx >= 0 && idx < values.length ? values[idx] : "";
    };

    // Skip rows with no OCS code and no dispensing UID (truly empty)
    const ocsCode = parseString(get("OCS Code"));
    const dispensingUID = parseString(get("Dispensing UID"));
    const productDesc = parseString(get("Product Description"));

    // Include rows that have either an OCS code or a dispensing UID (EPS items)
    if (!ocsCode && !dispensingUID && !productDesc) continue;

    items.push({
      ocsCode,
      dispensingMonth: parseString(get("Dispensing Month")),
      fragmentId: parseString(get("Fragment Id")),
      formNumber: parseNumber(get("Form Number")),
      itemNumber: parseNumber(get("Item Number")),
      elementId: parseNumber(get("Element Id")),
      formType: parseString(get("Form Type")),
      prescriberCode: parseString(get("Prescriber Code")),
      groupTypeDeclared: parseNumber(get("Group Type Declared")),
      chargeStatus: parseNumber(get("Charge Status")),
      chargesPayable: parseNumber(get("Charges Payable")),
      productDescription: productDesc,
      snomedCode: parseString(get("Snomed Code")),
      quantity: parseNumber(get("Quantity")),
      unitOfMeasure: parseString(get("Unit Of Measure")),
      packPrice: parseNumber(get("Pack Price")),
      packSize: parseNumber(get("Pack Size")),
      basicPrice: parseNumber(get("Basic Price")),
      paymentForConsumables: parseNumber(get("Payment for Consumables")),
      paymentForContainers: parseNumber(get("Payment for Containers")),
      nb: parseString(get("NB")),
      cc: parseString(get("CC")),
      da: parseString(get("DA")),
      daValue: parseNumber(get("DA Value")),
      ex: parseString(get("EX")),
      exValue: parseNumber(get("EX Value")),
      gn: parseString(get("GN")),
      ip: parseString(get("IP")),
      ipValue: parseNumber(get("IP Value")),
      lp: parseString(get("LP")),
      lpValue: parseNumber(get("LP Value")),
      mc: parseString(get("MC")),
      mcValue: parseNumber(get("MC Value")),
      mf: parseString(get("MF")),
      mi: parseString(get("MI")),
      miValue: parseNumber(get("MI Value")),
      mp: parseString(get("MP")),
      mpValue: parseNumber(get("MP Value")),
      nc: parseString(get("NC")),
      ncValue: parseNumber(get("NC Value")),
      nd: parseString(get("ND")),
      rb: parseString(get("RB")),
      rbValue: parseNumber(get("RB Value")),
      sf: parseString(get("SF")),
      sfValue: parseNumber(get("SF Value")),
      zd: parseString(get("ZD")),
      sdrProfFeeValue: parseNumber(get("SDR Professional Fee Value")),
      sdrProfFeeNumber: parseNumber(get("SDR Professional Fee Number")),
      zdrProfFeeValue: parseNumber(get("ZDR Professional Fee Value")),
      zdrProfFeeNumber: parseNumber(get("ZDR Professional Fee Number")),
      spUnlicensedMedsFeeValue: parseNumber(get("SP Unlicensed Meds Fee Value")),
      edUnlicensedMedsFeeValue: parseNumber(get("ED Unlicensed Meds Fee Value")),
      mfHosieryFeeValue: parseNumber(get("MF Hosiery Fee Value")),
      mfTrussFeeValue: parseNumber(get("MF Truss Fee Value")),
      mfBeltGirdleFeeValue: parseNumber(get("MF Belt and Girdle Fee Value")),
      methadoneFeeValue: parseNumber(get("Methadone Fee Value")),
      methadonePckgdDoseFeeValue: parseNumber(get("Methadone Pckgd Dose Fee Value")),
      homeDelSRApplAddFeeValue: parseNumber(get("Home Del SR Appl Add Fee Value")),
      homeDelHRApplAddFeeValue: parseNumber(get("Home Del HR Appl Add Fee Value")),
      cdSchedule2FeeValue: parseNumber(get("CD Schedule 2 Fee Value")),
      cdSchedule3FeeValue: parseNumber(get("CD Schedule 3 Fee Value")),
      expensiveItemFeeValue: parseNumber(get("Expensive Item Fee Value")),
      stomaCustomisationFeeValue: parseNumber(get("Stoma Customisation Fee Value")),
      dispensingUID: dispensingUID,
      nhsPatientNumber: parseString(get("NHS Patient Number")),
      sspVatValue: parseNumber(get("SSP Vat Value")),
      sspFeeValue: parseNumber(get("SSP Fee Value")),
      dra: parseString(get("DRA")),
      drb: parseString(get("DRB")),
      drc: parseString(get("DRC")),
      quantityPrescribed: parseNumber(get("Quantity Prescribed")),
      quantityDispensed: parseNumber(get("Quantity Dispensed")),
      opd: parseString(get("OPD")),
      advanceService: parseString(get("Advance Service")),
    });
  }

  return items;
}

export function calculateFP34Summary(items: PrescriptionItem[]): FP34Summary {
  if (items.length === 0) {
    return createEmptySummary();
  }

  const ocsCode = items.find((i) => i.ocsCode)?.ocsCode || "Unknown";
  const dispensingMonth = items.find((i) => i.dispensingMonth)?.dispensingMonth || "Unknown";

  // Track unique forms (by form number) and items
  const formSet = new Set<number>();
  const chargePaidFormSet = new Set<number>();
  const noChargeFormSet = new Set<number>();
  const epsFormSet = new Set<number>();
  const paperFormSet = new Set<number>();
  const formTypeCounts: Record<string, number> = {};

  let totalItems = 0;
  let chargePaidItems = 0;
  let noChargeItems = 0;
  let epsItems = 0;
  let paperItems = 0;

  let totalBasicPrice = 0;
  let totalConsumables = 0;
  let totalContainers = 0;
  let totalSdrProfFees = 0;
  let totalZdrProfFees = 0;
  let totalExpensiveItemFees = 0;
  let totalCdSchedule2Fees = 0;
  let totalCdSchedule3Fees = 0;
  let totalMethadoneFees = 0;
  let totalMethadonePckgdDoseFees = 0;
  let totalSspFees = 0;
  let totalHomeDelFees = 0;
  let totalStomaFees = 0;
  let totalUnlicensedMedsFees = 0;

  let expensiveItemsOver100Count = 0;
  let expensiveItemsOver100Value = 0;
  let expensiveItemsOver300Count = 0;
  let expensiveItemsOver300Value = 0;

  // Product aggregation
  const productMap = new Map<string, { items: number; totalValue: number }>();

  for (const item of items) {
    totalItems++;

    const formNum = item.formNumber;
    if (formNum > 0) {
      formSet.add(formNum);
    }

    // Charge status
    if (item.chargesPayable > 0) {
      chargePaidItems++;
      if (formNum > 0) chargePaidFormSet.add(formNum);
    } else {
      noChargeItems++;
      if (formNum > 0) noChargeFormSet.add(formNum);
    }

    // EPS vs Paper - EPS items have a dispensing UID, paper items have form types like FP10D
    const isEps = !!item.dispensingUID;
    if (isEps) {
      epsItems++;
      if (formNum > 0) epsFormSet.add(formNum);
    } else {
      paperItems++;
      if (formNum > 0) paperFormSet.add(formNum);
    }

    // Form type counts
    if (item.formType) {
      formTypeCounts[item.formType] = (formTypeCounts[item.formType] || 0) + 1;
    }

    // Financial
    totalBasicPrice += item.basicPrice;
    totalConsumables += item.paymentForConsumables;
    totalContainers += item.paymentForContainers;
    totalSdrProfFees += item.sdrProfFeeValue;
    totalZdrProfFees += item.zdrProfFeeValue;
    totalExpensiveItemFees += item.expensiveItemFeeValue;
    totalCdSchedule2Fees += item.cdSchedule2FeeValue;
    totalCdSchedule3Fees += item.cdSchedule3FeeValue;
    totalMethadoneFees += item.methadoneFeeValue;
    totalMethadonePckgdDoseFees += item.methadonePckgdDoseFeeValue;
    totalSspFees += item.sspFeeValue;
    totalHomeDelFees += item.homeDelSRApplAddFeeValue + item.homeDelHRApplAddFeeValue;
    totalStomaFees += item.stomaCustomisationFeeValue;
    totalUnlicensedMedsFees += item.spUnlicensedMedsFeeValue + item.edUnlicensedMedsFeeValue;

    // Expensive items
    if (item.basicPrice >= 100 && item.basicPrice < 300) {
      expensiveItemsOver100Count++;
      expensiveItemsOver100Value += item.basicPrice;
    } else if (item.basicPrice >= 300) {
      expensiveItemsOver300Count++;
      expensiveItemsOver300Value += item.basicPrice;
    }

    // Product aggregation
    if (item.productDescription && item.productDescription !== "None") {
      const existing = productMap.get(item.productDescription);
      if (existing) {
        existing.items++;
        existing.totalValue += item.basicPrice;
      } else {
        productMap.set(item.productDescription, { items: 1, totalValue: item.basicPrice });
      }
    }
  }

  const totalProfessionalFees = totalSdrProfFees + totalZdrProfFees;

  // Top products by total value
  const topProducts = Array.from(productMap.entries())
    .map(([description, data]) => ({ description, ...data }))
    .sort((a, b) => b.totalValue - a.totalValue)
    .slice(0, 20);

  return {
    ocsCode,
    dispensingMonth,
    dispensingMonthLabel: formatDispensingMonth(dispensingMonth),
    totalForms: formSet.size,
    totalItems,
    chargePaidForms: chargePaidFormSet.size,
    chargePaidItems,
    noChargeForms: noChargeFormSet.size,
    noChargeItems,
    epsItems,
    epsForms: epsFormSet.size,
    paperItems,
    paperForms: paperFormSet.size,
    totalBasicPrice: Math.round(totalBasicPrice * 100) / 100,
    totalConsumables: Math.round(totalConsumables * 100) / 100,
    totalContainers: Math.round(totalContainers * 100) / 100,
    totalProfessionalFees: Math.round(totalProfessionalFees * 100) / 100,
    totalSdrProfFees: Math.round(totalSdrProfFees * 100) / 100,
    totalZdrProfFees: Math.round(totalZdrProfFees * 100) / 100,
    totalExpensiveItemFees: Math.round(totalExpensiveItemFees * 100) / 100,
    totalCdSchedule2Fees: Math.round(totalCdSchedule2Fees * 100) / 100,
    totalCdSchedule3Fees: Math.round(totalCdSchedule3Fees * 100) / 100,
    totalMethadoneFees: Math.round(totalMethadoneFees * 100) / 100,
    totalMethadonePckgdDoseFees: Math.round(totalMethadonePckgdDoseFees * 100) / 100,
    totalSspFees: Math.round(totalSspFees * 100) / 100,
    totalHomeDelFees: Math.round(totalHomeDelFees * 100) / 100,
    totalStomaFees: Math.round(totalStomaFees * 100) / 100,
    totalUnlicensedMedsFees: Math.round(totalUnlicensedMedsFees * 100) / 100,
    expensiveItemsOver100Count,
    expensiveItemsOver100Value: Math.round(expensiveItemsOver100Value * 100) / 100,
    expensiveItemsOver300Count,
    expensiveItemsOver300Value: Math.round(expensiveItemsOver300Value * 100) / 100,
    formTypeCounts,
    topProducts,
    items,
  };
}

// ── AI Discrepancy Analysis ──

export type InsightSeverity = "critical" | "warning" | "info" | "success";

export interface FP34Insight {
  id: string;
  severity: InsightSeverity;
  category: string;
  title: string;
  description: string;
  impact?: string; // financial impact if applicable
  items?: { description: string; detail: string }[];
}

export function analyseDiscrepancies(summary: FP34Summary): FP34Insight[] {
  const insights: FP34Insight[] = [];
  const items = summary.items;

  // 1. Items with zero basic price (possible pricing errors)
  const zeroPriceItems = items.filter((i) => i.basicPrice === 0 && i.productDescription && i.productDescription !== "None");
  if (zeroPriceItems.length > 0) {
    insights.push({
      id: "zero-price",
      severity: "critical",
      category: "Pricing",
      title: `${zeroPriceItems.length} item${zeroPriceItems.length > 1 ? "s" : ""} with zero NIC`,
      description: "These items have a basic price of £0.00, which means no drug reimbursement will be received. This could indicate a pricing issue or an endorsement-only item (e.g. DA claim).",
      impact: "Potential lost reimbursement if items were dispensed but not priced",
      items: zeroPriceItems.slice(0, 5).map((i) => ({
        description: i.productDescription || "Unknown",
        detail: `Form ${i.formNumber}, Qty: ${i.quantity} ${i.unitOfMeasure || ""}`,
      })),
    });
  }

  // 2. Professional fee discrepancies - items without professional fees
  const noFeeItems = items.filter((i) =>
    i.productDescription && i.productDescription !== "None" &&
    i.basicPrice > 0 &&
    i.zdrProfFeeValue === 0 && i.sdrProfFeeValue === 0
  );
  if (noFeeItems.length > 0) {
    const estimatedLoss = noFeeItems.length * 1.46; // approx single activity fee
    insights.push({
      id: "missing-prof-fee",
      severity: "warning",
      category: "Professional Fees",
      title: `${noFeeItems.length} item${noFeeItems.length > 1 ? "s" : ""} without professional fee`,
      description: "These dispensed items have a basic price but no SDR or ZDR professional fee attached. Each item should normally attract a single activity fee.",
      impact: formatGBP(estimatedLoss) + " estimated missing professional fees",
      items: noFeeItems.slice(0, 5).map((i) => ({
        description: i.productDescription || "Unknown",
        detail: `NIC: ${formatGBP(i.basicPrice)}, Form: ${i.formType || "EPS"}`,
      })),
    });
  }

  // 3. Pack size vs quantity discrepancy (broken bulk / potential endorsement issue)
  const brokenBulk = items.filter((i) =>
    i.packSize > 0 && i.quantity > 0 && i.quantity < i.packSize && i.basicPrice > 0
  );
  if (brokenBulk.length > 0) {
    const overchargeTotal = brokenBulk.reduce((sum, i) => {
      const unitPrice = i.packPrice / i.packSize;
      const expectedPrice = unitPrice * i.quantity;
      return sum + (i.basicPrice - expectedPrice);
    }, 0);
    insights.push({
      id: "broken-bulk",
      severity: "info",
      category: "Quantity / Pack Size",
      title: `${brokenBulk.length} item${brokenBulk.length > 1 ? "s" : ""} dispensed as broken bulk`,
      description: "Quantity dispensed is less than the full pack size. The NIC may have been adjusted pro-rata. Check that endorsements are correct to avoid under-reimbursement.",
      impact: overchargeTotal !== 0 ? `${formatGBP(Math.abs(overchargeTotal))} price adjustment from broken bulk` : undefined,
      items: brokenBulk.slice(0, 5).map((i) => ({
        description: i.productDescription || "Unknown",
        detail: `Dispensed ${i.quantity}/${i.packSize} (pack), NIC: ${formatGBP(i.basicPrice)} vs pack: ${formatGBP(i.packPrice)}`,
      })),
    });
  }

  // 4. Items with DRA/DRB/DRC flags (repeat dispensing markers)
  const draItems = items.filter((i) => i.dra === "DRA");
  const drbItems = items.filter((i) => i.drb === "DRB");
  const drcItems = items.filter((i) => i.drc === "DRC");
  if (draItems.length > 0 || drbItems.length > 0 || drcItems.length > 0) {
    const total = draItems.length + drbItems.length + drcItems.length;
    insights.push({
      id: "repeat-dispensing",
      severity: "info",
      category: "Repeat Dispensing",
      title: `${total} repeat dispensing item${total > 1 ? "s" : ""} detected`,
      description: `DRA (first issue): ${draItems.length}, DRB (subsequent): ${drbItems.length}, DRC (last issue): ${drcItems.length}. Repeat dispensing items follow different payment timelines and attract separate fees.`,
    });
  }

  // 5. Expensive items check
  if (summary.expensiveItemsOver100Count > 0 || summary.expensiveItemsOver300Count > 0) {
    const expensiveWithNoFee = items.filter((i) => i.basicPrice >= 100 && i.expensiveItemFeeValue === 0);
    if (expensiveWithNoFee.length > 0) {
      insights.push({
        id: "expensive-no-fee",
        severity: "warning",
        category: "Expensive Items",
        title: `${expensiveWithNoFee.length} expensive item${expensiveWithNoFee.length > 1 ? "s" : ""} without expensive item fee`,
        description: "Items with NIC of £100 or more should attract an expensive item fee. These items appear to be missing this fee, which could indicate an endorsement issue.",
        impact: formatGBP(expensiveWithNoFee.length * 5.16) + " potential missing expensive item fees",
        items: expensiveWithNoFee.slice(0, 5).map((i) => ({
          description: i.productDescription || "Unknown",
          detail: `NIC: ${formatGBP(i.basicPrice)}, Expensive Fee: ${formatGBP(i.expensiveItemFeeValue)}`,
        })),
      });
    }
  }

  // 6. Controlled drug schedule fees
  const cdItems = items.filter((i) => i.cdSchedule2FeeValue > 0 || i.cdSchedule3FeeValue > 0);
  if (cdItems.length > 0) {
    const totalCdFees = cdItems.reduce((s, i) => s + i.cdSchedule2FeeValue + i.cdSchedule3FeeValue, 0);
    insights.push({
      id: "cd-fees",
      severity: "info",
      category: "Controlled Drugs",
      title: `${cdItems.length} controlled drug${cdItems.length > 1 ? "s" : ""} with schedule fees`,
      description: `Controlled drug schedule fees totalling ${formatGBP(totalCdFees)} have been applied. Verify that all Schedule 2 and 3 items have been correctly identified.`,
    });
  }

  // 7. Charge status analysis - high exempt rate
  if (summary.totalItems > 0) {
    const exemptRate = summary.noChargeItems / summary.totalItems;
    if (exemptRate > 0.95) {
      insights.push({
        id: "high-exempt",
        severity: "info",
        category: "Charge Status",
        title: `${(exemptRate * 100).toFixed(1)}% of items are charge-exempt`,
        description: `Only ${summary.chargePaidItems} out of ${summary.totalItems} items had a patient charge. This is a very high exemption rate. This is normal for some areas but worth monitoring for post-payment verification risk.`,
      });
    }
  }

  // 8. Container allowance check
  const itemsWithContainers = items.filter((i) => i.paymentForContainers > 0);
  const itemsWithoutContainers = items.filter((i) => i.paymentForContainers === 0 && i.basicPrice > 0 && i.productDescription !== "None");
  if (itemsWithContainers.length > 0 && summary.totalContainers > 0) {
    insights.push({
      id: "containers",
      severity: "success",
      category: "Container Allowance",
      title: `${itemsWithContainers.length} item${itemsWithContainers.length > 1 ? "s" : ""} claimed container allowance (${formatGBP(summary.totalContainers)})`,
      description: `Container allowance of ${formatGBP(summary.totalContainers)} claimed across ${itemsWithContainers.length} items. ${itemsWithoutContainers.length} items did not claim container allowance.`,
    });
  }

  // 9. SF/ZD endorsement flags
  const sfItems = items.filter((i) => i.sf === "SF");
  const zdItems = items.filter((i) => i.zd === "ZD");
  if (sfItems.length > 0) {
    insights.push({
      id: "sf-endorsement",
      severity: "info",
      category: "Endorsements",
      title: `${sfItems.length} item${sfItems.length > 1 ? "s" : ""} with SF (Special Fee) endorsement`,
      description: "Items endorsed with SF attract an additional special fee. Verify these are correctly endorsed.",
    });
  }

  // 10. Overall health check
  const criticalCount = insights.filter((i) => i.severity === "critical").length;
  const warningCount = insights.filter((i) => i.severity === "warning").length;
  if (criticalCount === 0 && warningCount === 0) {
    insights.push({
      id: "all-clear",
      severity: "success",
      category: "Overall",
      title: "No critical discrepancies detected",
      description: "The prescription item report appears consistent. No major pricing, fee, or endorsement issues were found.",
    });
  }

  return insights;
}

function formatGBP(value: number): string {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 2 }).format(value);
}

function createEmptySummary(): FP34Summary {
  return {
    ocsCode: "", dispensingMonth: "", dispensingMonthLabel: "",
    totalForms: 0, totalItems: 0,
    chargePaidForms: 0, chargePaidItems: 0,
    noChargeForms: 0, noChargeItems: 0,
    epsItems: 0, epsForms: 0, paperItems: 0, paperForms: 0,
    totalBasicPrice: 0, totalConsumables: 0, totalContainers: 0,
    totalProfessionalFees: 0, totalSdrProfFees: 0, totalZdrProfFees: 0,
    totalExpensiveItemFees: 0, totalCdSchedule2Fees: 0, totalCdSchedule3Fees: 0,
    totalMethadoneFees: 0, totalMethadonePckgdDoseFees: 0,
    totalSspFees: 0, totalHomeDelFees: 0, totalStomaFees: 0, totalUnlicensedMedsFees: 0,
    expensiveItemsOver100Count: 0, expensiveItemsOver100Value: 0,
    expensiveItemsOver300Count: 0, expensiveItemsOver300Value: 0,
    formTypeCounts: {}, topProducts: [], items: [],
  };
}
